import React from "react"
import { useParams, Navigate } from "react-router-dom"
import getAppConfig from "~/utils/getAppConfig"
import { useToast } from "~/utils/Toast"

import { apiWorkspaceContacts, apiWorkspaceContactCategories } from "~/routes"
import useFetchResource from "~/hooks/useFetchResource"

import PageContainer from "~/library/PageContainer"
import LoadingWrapper from "~/library/LoadingWrapper"
import ContactForm from "./ContactForm"
import type Contact from "~/types/Contact"

type ContactCategoryOption = {
    id: string
    name: string
}

const EditContact = () => {
    const { venueSlug } = getAppConfig()
    const { id } = useParams<{ id: string }>()
    const [isUpdated, setIsUpdated] = React.useState(false)
    const toast = useToast()

    const { data: contact, isLoading: contactLoading } = useFetchResource<
    { contact: Contact },
    "contact"
    >(apiWorkspaceContacts.show.path({ venue_slug: venueSlug, id: id! }), "contact", {})

    const { data: contactCategories, isLoading: categoriesLoading } = useFetchResource<
    ContactCategoryOption[],
    "contact_categories"
    >(apiWorkspaceContactCategories.index.path({ venue_slug: venueSlug }), "contact_categories", {})

    const handleSubmit = async (formValues: any) => {
        try {
            const url = apiWorkspaceContacts.update.path({
                venue_slug: venueSlug,
                id: id!
            })

            const response = await fetch(url, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    contact: formValues,
                }),
            })

            const result = await response.json()

            if (result.status === 'ok') {
                setIsUpdated(true)
                toast("Contact updated successfully", "success")
            } else {
                toast(
                    `Error updating contact. ${result.errors?.join(", ") || "Please try again"}`,
                    "danger"
                )
            }
        } catch (error) {
            console.error("Error:", error)
            toast("Error updating contact. Please try again", "danger")
        }
    }

    if (isUpdated) {
        return <Navigate to={`/contacts/${id}`} />
    }

    const isLoading = contactLoading || categoriesLoading

    return (
        <PageContainer>
            <LoadingWrapper isLoading={isLoading}>
                <div className="row h-100">
                    <div className="col-8 offset-2 align-center mt-4">
                        <h3 className="mb-3">Edit Contact</h3>
                        {contact && contactCategories && (
                            <ContactForm
                                onSubmit={handleSubmit}
                                initialValues={{
                                    firstName: contact.firstName,
                                    lastName: contact.lastName,
                                    email: contact.email,
                                    phone: contact.phone || "",
                                    role: contact.role || "",
                                    contactCategoryId: contact.contactCategory?.id || "",
                                    accountId: contact.account?.id || "",
                                }}
                                contactCategories={contactCategories}
                                initialAccount={contact.account ? {
                                    id: contact.account.id,
                                    name: contact.account.name
                                } : null}
                                isEditing={true}
                            />
                        )}
                    </div>
                </div>
            </LoadingWrapper>
        </PageContainer>
    )
}

export default EditContact
